import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';

import type { PhotoCapture } from '../../../../utils/exif/readPhotoCapture';
import { loadPhotoCapture, type LocatableImage } from '../../../../utils/photoLocation';
import { resolvePkAtPosition, type PkResolution } from '../../../../utils/resolvePkAtPosition';

/** `PhotoCapture` con coordenadas seguro. */
export type LocatedCapture = PhotoCapture & { latitude: number; longitude: number };

export type PhotoLocationState =
  /** Sin el flag: no se lee nada. */
  | { status: 'off' }
  | { status: 'loading' }
  /** Foto legacy de Cloudinary: ni original en Hetzner ni dato del servidor. */
  | { status: 'no-original' }
  | { status: 'offline' }
  /** No se pudo leer (HTTP, CORS…). Distinto de «no tiene». */
  | { status: 'error' }
  /** Leída, sin GPS (o a ceros). */
  | { status: 'no-gps'; capture: PhotoCapture }
  /**
   * Con GPS. `resolution` es `null` si la búsqueda del PK falló: la ubicación
   * se enseña igual.
   */
  | { status: 'ready'; capture: LocatedCapture; resolution: PkResolution | null };

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * Dónde se hizo la foto que enseña el visor. Spec 2026-09-23 fotos
 * geolocalizadas, § 16.
 *
 * La ubicación sale del servidor si ya la leyó (`capture` en `READ`, § 18) y,
 * si no, del EXIF del ORIGINAL (128 KB): ver `loadPhotoCapture`. Si trae GPS,
 * busca carretera y PK con `resolvePkAtPosition` —la misma de «Autolocalizar
 * PK»— SIN concesión: el backend acota a las vías del tenant, y sin red tira de
 * la malla guardada. Con el dato del servidor ya en la caché, funciona también
 * sin red.
 *
 * 🔴 Una lectura por foto: al cambiar de foto o cerrar el visor, la anterior
 * se cancela y su respuesta, si llega tarde, se descarta. Si no, un visor que
 * pasa rápido de una foto a otra podría pintar la ubicación de la anterior.
 *
 */
export function usePhotoLocation(
  image: LocatableImage | null | undefined,
  enabled: boolean,
): PhotoLocationState {
  const client = useApolloClient();
  const [state, setState] = useState<PhotoLocationState>({ status: 'off' });
  const publicUrl = image?.publicUrl;
  const serverCapture = image?.capture;

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'off' });
      return;
    }
    const fromServer = serverCapture?.status === 'READ';
    if (!fromServer && !publicUrl) {
      setState({ status: 'no-original' });
      return;
    }
    // Sin red solo hay algo que enseñar si el servidor ya lo dio.
    if (!fromServer && isOffline()) {
      setState({ status: 'offline' });
      return;
    }

    const controller = new AbortController();
    let alive = true;
    setState({ status: 'loading' });

    void (async () => {
      let capture: PhotoCapture | null;
      try {
        capture = await loadPhotoCapture({ publicUrl, capture: serverCapture }, controller.signal);
      } catch (err) {
        if (!alive) return;
        if (isOffline()) {
          setState({ status: 'offline' });
          return;
        }
        // Un CORS mal configurado en un bucket solo se ve así: que avise.
        Sentry.captureException(err, {
          tags: { feature: 'photo-location', step: 'read-exif' },
          level: 'warning',
        });
        setState({ status: 'error' });
        return;
      }
      if (!alive) return;
      if (!capture) {
        setState({ status: 'no-original' });
        return;
      }

      if (capture.latitude === null || capture.longitude === null) {
        setState({ status: 'no-gps', capture });
        return;
      }
      const located = capture as LocatedCapture;

      let resolution: PkResolution | null = null;
      try {
        resolution = await resolvePkAtPosition(client, {
          lat: located.latitude,
          lng: located.longitude,
        });
      } catch {
        // Sin PK la foto sigue teniendo ubicación: coordenadas, mapa y enlace.
        resolution = null;
      }
      if (!alive) return;
      setState({ status: 'ready', capture: located, resolution });
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [client, enabled, publicUrl, serverCapture]);

  return state;
}
